import { useMemo, type JSX } from 'react';

import clsx from 'clsx';

import { SHIMMER_CLASSES } from 'DesignSystem2/Skeleton/constants';
import { Body } from 'DesignSystem3/Text/Body';
import type { KpowHistoryPoint } from 'react_pages/AmericanPowerIndex/api/kpowApi';

import { partyColorBySign } from '../kpowColors';
import { formatScore, formatStatDate, MONTH_MS, pointDate } from '../kpowFormat';

interface ChartStatsProps {
    points: KpowHistoryPoint[];
    /** Render shimmer cells while the first history fetch is in flight. */
    loading?: boolean;
}

interface StatBlock {
    label: string;
    value: string;
    /** Trailing date: when the extreme reading occurred. */
    date?: string;
    /** Party color for the value; undefined at neutral. */
    color?: string;
}

const extremePoint = (pts: KpowHistoryPoint[], direction: 'min' | 'max'): KpowHistoryPoint | null => {
    if (pts.length === 0) return null;
    return pts.reduce((best, p) => {
        const betterMin = direction === 'min' && p.signed_value < best.signed_value;
        const betterMax = direction === 'max' && p.signed_value > best.signed_value;
        return betterMin || betterMax ? p : best;
    }, pts[0]);
};

const toStat = (label: string, point: KpowHistoryPoint | null): StatBlock => ({
    label,
    value: point ? formatScore(point) : '–',
    date: point ? formatStatDate(point.end_period_ts) : undefined,
    color: point ? partyColorBySign(point.signed_value) : undefined,
});

/**
 * Four footer cells laid out 2x2 (Dem row / Rep row) x (1M / 6M), per Figma 745:2553.
 *
 * `points` is the FULL-resolution history (every hourly reading) with the live snapshot appended as
 * the rightmost point (see AmericanPowerIndexContent). The chart's line-only downsample never reaches
 * here, so extremes are computed at full precision.
 *
 * Each cell is the party's most extreme reading within the window (the furthest-Democratic / furthest-
 * Republican the index got, with the date it happened). The 1M cells scan the last month; the 6M cells
 * scan the full fetched window (the API caps history at ~180 days).
 */
const buildStats = (points: KpowHistoryPoint[]): StatBlock[] => {
    if (points.length === 0) return [];
    const monthCutoff = Date.now() - MONTH_MS;
    const monthPoints = points.filter((p) => pointDate(p.end_period_ts).getTime() >= monthCutoff);
    const withinMonth = monthPoints.length > 0 ? monthPoints : points;
    return [
        toStat('Dem 1M change', extremePoint(withinMonth, 'min')),
        toStat('Dem 6M high', extremePoint(points, 'min')),
        toStat('Rep 1M change', extremePoint(withinMonth, 'max')),
        toStat('Rep 6M high', extremePoint(points, 'max')),
    ];
};

// Fixed 222px cell so the two cells pack to the left (per Figma) instead of stretching across the
// card; full-width on phones so they stack cleanly.
const CELL_WIDTH = 'w-[222px] max-w-full sm:w-full';

/** One cell: label on the left, value + date pinned right in fixed columns so cells line up. */
const StatCell = ({ stat }: { stat: StatBlock }): JSX.Element => (
    <div className={clsx('flex items-baseline justify-between gap-2', CELL_WIDTH)}>
        <Body.x20 className="min-w-0 truncate text-text-x20">{stat.label}</Body.x20>
        <div className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
            <Body.x20
                className="w-[48px] text-right tabular-nums"
                style={stat.color ? { color: stat.color } : undefined}
            >
                {stat.value}
            </Body.x20>
            <Body.x20 className="w-[46px] tabular-nums text-text-x20">{stat.date ?? ''}</Body.x20>
        </div>
    </div>
);

const SkeletonCell = (): JSX.Element => (
    <div className={clsx('flex items-center justify-between gap-2', CELL_WIDTH)}>
        <div className={clsx(SHIMMER_CLASSES, 'h-[13px] w-[96px] rounded-x10')} />
        <div className="flex shrink-0 items-center gap-1">
            <div className={clsx(SHIMMER_CLASSES, 'h-[13px] w-[48px] rounded-x10')} />
            <div className={clsx(SHIMMER_CLASSES, 'h-[13px] w-[46px] rounded-x10')} />
        </div>
    </div>
);

/**
 * Footer stat grid: two rows (Dem / Rep) each with a 1M and 6M cell, split by hairlines above each
 * row and below the block. Stacks to one column on phones so the cells never overflow the card.
 */
const ChartStats = ({ points, loading }: ChartStatsProps): JSX.Element | null => {
    // ChartCard re-renders on every scrub frame; the extremes only depend on `points`.
    const stats = useMemo(() => buildStats(points), [points]);

    if (stats.length === 0) {
        if (!loading) return null;
        return (
            <div className="flex w-full flex-col gap-1.5 border-b border-solid border-stroke-x30 pb-1.5">
                <div className="flex flex-wrap gap-x-[90px] gap-y-1.5 border-t border-solid border-stroke-x40 pt-1.5">
                    <SkeletonCell />
                    <SkeletonCell />
                </div>
                <div className="flex flex-wrap gap-x-[90px] gap-y-1.5 border-t border-solid border-stroke-x40 pt-1.5">
                    <SkeletonCell />
                    <SkeletonCell />
                </div>
            </div>
        );
    }

    const [demMonth, demSixMonth, repMonth, repSixMonth] = stats;
    return (
        <div className="flex w-full flex-col gap-1.5 border-b border-solid border-stroke-x30 pb-1.5">
            <div className="flex flex-wrap gap-x-[90px] gap-y-1.5 border-t border-solid border-stroke-x40 pt-1.5">
                <StatCell stat={demMonth} />
                <StatCell stat={demSixMonth} />
            </div>
            <div className="flex flex-wrap gap-x-[90px] gap-y-1.5 border-t border-solid border-stroke-x40 pt-1.5">
                <StatCell stat={repMonth} />
                <StatCell stat={repSixMonth} />
            </div>
        </div>
    );
};

export default ChartStats;
